import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
    enrichCounterOfferNotificationMessage,
    extractNotificationEventId,
    extractNotificationEventName,
    isCounterOfferNotificationPayload,
} from 'src/app/shared/utils/notification-alert.helpers';
import { CurrencyInfo, CurrencyService } from './currency.service';
import { SocketService } from './socket.service';
import { VendorCounterOfferEventNameService } from './vendor-counter-offer-event-name.service';
import { VendorQuotationCurrencyContextService } from './vendor-quotation-currency-context.service';

type CounterOfferAmount = string | number | null;

/**
 * App-wide counter-offer toasts (any route / tab).
 * - `in_app_notification` — global vendor channel (works off quotation tab).
 * - `counterOfferUpdate` — enriches message when user is on the quotation room.
 */
@Injectable({ providedIn: 'root' })
export class VendorCounterOfferSocketToastService {
    private static readonly EVENT_DETAILS_ROUTE_ID = /event-details\/[^/]+\/([^/?#]+)/i;
    private static readonly COUNTER_OFFER_AMOUNT = /counter offer of\s+([\d.,]+)/i;

    private readonly socketService = inject(SocketService);
    private readonly toastr = inject(ToastrService);
    private readonly currencyService = inject(CurrencyService);
    private readonly quotationCurrencyContext = inject(VendorQuotationCurrencyContextService);
    private readonly counterOfferEventNameService = inject(VendorCounterOfferEventNameService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    private readonly recentToastKeys = new Map<string, number>();

    constructor() {
        this.socketService.counterOfferVendorToastData
            .pipe(filter((d: any) => d != null), takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => this.showFromCounterOfferSocket(data));

        this.socketService.socketNotificationData
            .pipe(
                filter(
                    (data: any) =>
                        !!data &&
                        (!!data.notification || !!(data.title && data.message)) &&
                        isCounterOfferNotificationPayload(data)
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((data) => this.showFromInAppNotification(data));
    }

    private showFromCounterOfferSocket(socketPayload: any): void {
        const type = this.normalizeCounterOfferType(socketPayload);
        if (!this.shouldToastForType(type)) {
            return;
        }

        const eventId = this.resolveEventId(socketPayload);
        const eventName = this.resolveEventName(socketPayload, undefined, undefined, eventId);
        const inner = socketPayload?.data ?? socketPayload;
        const amount = this.extractAmount(inner, '');

        const message = this.buildMessage(amount, eventName, '', socketPayload, inner, eventId);
        const title = 'Counter Offer Received';
        this.showToast(title, message, this.buildDedupeKey(eventId, amount, message));
    }

    private showFromInAppNotification(data: any): void {
        const notification = data.notification ?? data;
        const meta = notification.metadata ?? notification.meta_data ?? notification.meta ?? {};
        const type = this.normalizeCounterOfferType(data, notification);
        if (!this.shouldToastForType(type)) {
            return;
        }

        const baseMessage = String(notification.message ?? data.message ?? '').trim();
        const eventId = this.resolveEventId(data, notification, meta);
        const eventName = this.resolveEventName(data, notification, meta, eventId);
        const inner = data?.data ?? data;
        const amount = this.extractAmount(inner, baseMessage);
        const message = this.buildMessage(amount, eventName, baseMessage, data, inner, eventId);

        const title = String(notification.title ?? data.title ?? 'Counter Offer Received').trim() || 'Counter Offer Received';
        const needsDeferredName =
            !eventName && /\bfor\s+event\s*\.?$/i.test(message) && /\bfor\s+event\s*\.?$/i.test(baseMessage);

        if (needsDeferredName) {
            setTimeout(() => {
                const deferredName = this.resolveEventName(data, notification, meta, eventId);
                const deferredMessage = this.buildMessage(amount, deferredName, baseMessage, data, inner, eventId);
                this.showToast(title, deferredMessage, this.buildDedupeKey(eventId, amount, deferredMessage));
            }, 300);
            return;
        }

        this.showToast(title, message, this.buildDedupeKey(eventId, amount, message));
    }

    private normalizeCounterOfferType(data: any, notification?: any): string {
        return String(
            data?.type ??
                notification?.type ??
                notification?.alert_type ??
                notification?.alertType ??
                data?.data?.type ??
                ''
        ).toUpperCase();
    }

    private shouldToastForType(type: string): boolean {
        if (!type.includes('COUNTER_OFFER')) {
            return false;
        }
        return type !== 'COUNTER_OFFER_ACCEPTED' && type !== 'COUNTER_OFFER_REJECTED';
    }

    private resolveEventId(data: any, notification?: any, meta?: any): string {
        const fromPayload = extractNotificationEventId(data, notification, meta);
        if (fromPayload) {
            return fromPayload;
        }
        return this.extractEventIdFromRoute();
    }

    private resolveEventName(
        data: any,
        notification?: any,
        meta?: any,
        eventId?: string
    ): string {
        const fromPayload = extractNotificationEventName(data, notification, meta);
        if (fromPayload) {
            const id = eventId ?? this.resolveEventId(data, notification, meta);
            if (id) {
                this.counterOfferEventNameService.remember(id, fromPayload);
            }
            return fromPayload;
        }

        const id = eventId ?? this.resolveEventId(data, notification, meta);
        const remembered = this.counterOfferEventNameService.lookup(id);
        if (remembered) {
            return remembered;
        }
        return this.counterOfferEventNameService.lookupRecent();
    }

    private extractEventIdFromRoute(): string {
        const match = VendorCounterOfferSocketToastService.EVENT_DETAILS_ROUTE_ID.exec(this.router.url);
        return String(match?.[1] ?? '').trim();
    }

    private extractAmount(inner: any, message: string): CounterOfferAmount {
        const amount =
            inner?.counterOfferData?.amount ??
            inner?.counter_offer?.amount ??
            inner?.counterOffer?.amount ??
            inner?.counter_offer_data?.amount ??
            inner?.metadata?.amount ??
            inner?.meta_data?.amount;

        if (amount != null && amount !== '') {
            return amount;
        }

        const match = VendorCounterOfferSocketToastService.COUNTER_OFFER_AMOUNT.exec(String(message));
        return match ? match[1].replace(/,/g, '') : null;
    }

    private currencyInfoFromPayload(socketPayload: any, inner: any, eventId: string): CurrencyInfo | null {
        const notification = socketPayload?.notification ?? inner?.notification;
        const meta = notification?.metadata ?? notification?.meta_data ?? notification?.meta ?? inner?.metadata ?? inner?.meta_data ?? {};

        const c =
            inner?.counterOfferData?.currency ??
            inner?.counter_offer?.currency ??
            inner?.counterOffer?.currency ??
            meta?.currency ??
            socketPayload?.data?.eventData?.currency ??
            socketPayload?.eventData?.currency;

        if (c) {
            if (typeof c === 'string') {
                const code = c.trim().toUpperCase();
                return {
                    code,
                    symbol: this.currencyService.getCurrencySymbol(code),
                    name: code,
                };
            }
            return this.currencyService.extractCurrencyFromApi(c);
        }

        const fromQuotationTab = this.quotationCurrencyContext.getEventDisplayCurrency(eventId);
        if (fromQuotationTab?.code) {
            return fromQuotationTab;
        }

        const vendorCurrency =
            socketPayload?.data?.eventData?.event_vendors?.[0]?.currency ??
            socketPayload?.eventData?.event_vendors?.[0]?.currency;
        const vendorCode = vendorCurrency?.currency;
        if (vendorCode != null && String(vendorCode).trim() !== '') {
            const code = String(vendorCode).trim().toUpperCase();
            return {
                code,
                symbol: this.currencyService.getCurrencySymbol(code),
                name: code,
            };
        }

        return null;
    }

    private formatCounterOfferAmount(
        amount: CounterOfferAmount,
        socketPayload: any,
        inner: any,
        eventId: string
    ): string {
        if (amount == null || amount === '') {
            return '';
        }
        const currency = this.currencyInfoFromPayload(socketPayload, inner, eventId);
        return this.currencyService.formatCurrencyCompact(amount, currency, { skipConversion: true });
    }

    private injectFormattedAmountIntoMessage(message: string, formattedAmount: string): string {
        if (!formattedAmount) {
            return message;
        }
        return String(message).replace(
            VendorCounterOfferSocketToastService.COUNTER_OFFER_AMOUNT,
            `counter offer of ${formattedAmount}`
        );
    }

    private buildMessage(
        amount: CounterOfferAmount,
        eventName: string,
        fallbackMessage: string,
        socketPayload: any,
        inner: any,
        eventId: string
    ): string {
        const formattedAmount = this.formatCounterOfferAmount(amount, socketPayload, inner, eventId);
        const base = String(fallbackMessage ?? '').trim();
        let message: string;
        if (base) {
            message = enrichCounterOfferNotificationMessage(base, eventName);
        } else if (formattedAmount) {
            message = enrichCounterOfferNotificationMessage(
                `You have received a counter offer of ${formattedAmount} for event`,
                eventName
            );
        } else if (amount != null && amount !== '') {
            message = enrichCounterOfferNotificationMessage(
                `You have received a counter offer of ${amount} for event`,
                eventName
            );
        } else {
            message = enrichCounterOfferNotificationMessage('You have received a counter offer for event', eventName);
        }
        if (formattedAmount) {
            message = this.injectFormattedAmountIntoMessage(message, formattedAmount);
        }
        return message;
    }

    private buildDedupeKey(
        eventId: string,
        amount: CounterOfferAmount,
        message: string
    ): string {
        if (eventId && amount != null && amount !== '') {
            return `CO|${eventId}|${amount}`;
        }
        return `CO|${String(message).trim()}`;
    }

    private showToast(title: string, message: string, dedupeKey: string): void {
        const text = String(message ?? '').trim();
        if (!text) {
            return;
        }
        if (this.isRecentDuplicate(dedupeKey)) {
            return;
        }
        this.toastr.info(text, title);
    }

    private isRecentDuplicate(key: string): boolean {
        const now = Date.now();
        const ttlMs = 15000;
        const last = this.recentToastKeys.get(key);
        if (last != null && now - last < ttlMs) {
            return true;
        }
        this.recentToastKeys.set(key, now);
        return false;
    }
}
